import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider } from "@/context/AuthContext";
import { ContentProvider } from "@/context/ContentContext";
import PublicLayout from "@/components/layout/PublicLayout";
import ProtectedRoute from "@/pages/portal/ProtectedRoute";
import PortalLayout from "@/pages/portal/PortalLayout";

// Public pages
import Home from "@/pages/public/Home";
const About = lazy(() => import("@/pages/public/About"));
const Why = lazy(() => import("@/pages/public/Why"));
const Contact = lazy(() => import("@/pages/public/Contact"));
const Partners = lazy(() => import("@/pages/public/Partners"));
const Careers = lazy(() => import("@/pages/public/Careers"));
const SolutionsIndex = lazy(() => import("@/pages/public/SolutionsIndex"));
const SolutionDetail = lazy(() => import("@/pages/public/SolutionDetail"));
const PlatformsIndex = lazy(() => import("@/pages/public/PlatformsIndex"));
const PlatformDetail = lazy(() => import("@/pages/public/PlatformDetail"));
const IndustriesIndex = lazy(() => import("@/pages/public/IndustriesIndex"));
const IndustryDetail = lazy(() => import("@/pages/public/IndustryDetail"));
const ResourcesIndex = lazy(() => import("@/pages/public/ResourcesIndex"));
const ResourceCategory = lazy(() => import("@/pages/public/ResourceCategory"));
const CaseStudyDetail = lazy(() => import("@/pages/public/CaseStudyDetail"));
const PrivacyPolicy = lazy(() => import("@/pages/public/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("@/pages/public/TermsOfUse"));
const CookiePolicy = lazy(() => import("@/pages/public/CookiePolicy"));
const NotFound = lazy(() => import("@/pages/public/NotFound"));
const Book = lazy(() => import("@/pages/public/Book"));
const EcousoProfile = lazy(() => import("@/pages/ecouso/Profile"));

// Portal pages
const Login = lazy(() => import("@/pages/portal/Login"));
const ForgotPassword = lazy(() => import("@/pages/portal/ForgotPassword"));
const Overview = lazy(() => import("@/pages/portal/Overview"));
const ResourceModule = lazy(() => import("@/pages/portal/ResourceModule"));
const Tickets = lazy(() => import("@/pages/portal/Tickets"));
const ServiceRequests = lazy(() => import("@/pages/portal/ServiceRequests"));
const Notifications = lazy(() => import("@/pages/portal/Notifications"));
const AccountSettings = lazy(() => import("@/pages/portal/AccountSettings"));
const AdminContent = lazy(() => import("@/pages/portal/AdminContent"));
const Billing = lazy(() => import("@/pages/portal/Billing"));
const CbConsole = lazy(() => import("@/pages/portal/CbConsole"));

function Fallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Loader2 className="animate-spin text-primary" size={30} />
    </div>
  );
}

// When the app is served from the dedicated executive-profile subdomain
// (e.g. ecouso.cloudbeaker.com), serve the profile page directly at the root
// so visitors land on it without needing the /ecouso path.
function isEcousoHost() {
  if (typeof window === "undefined") return false;
  return /^ecouso\./i.test(window.location.hostname);
}

function App() {
  return (
    <AuthProvider>
      <ContentProvider>
      <BrowserRouter>
        <Suspense fallback={<Fallback />}>
          {isEcousoHost() ? (
            <Routes>
              <Route path="*" element={<EcousoProfile />} />
            </Routes>
          ) : (
          <Routes>
            {/* Standalone executive profile (subdomain: ecouso.cloudbeaker.com) */}
            <Route path="/ecouso" element={<EcousoProfile />} />

            {/* Public */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/why" element={<Why />} />
              <Route path="/leadership" element={<Navigate to="/why#leadership" replace />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/book" element={<Book />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/solutions" element={<SolutionsIndex />} />
              <Route path="/solutions/:slug" element={<SolutionDetail />} />
              <Route path="/platforms" element={<PlatformsIndex />} />
              <Route path="/platforms/:slug" element={<PlatformDetail />} />
              <Route path="/industries" element={<IndustriesIndex />} />
              <Route path="/industries/:slug" element={<IndustryDetail />} />
              <Route path="/resources" element={<ResourcesIndex />} />
              <Route path="/resources/case-studies/:slug" element={<CaseStudyDetail />} />
              <Route path="/resources/:category" element={<ResourceCategory />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* Portal auth */}
            <Route path="/client-portal" element={<Navigate to="/portal" replace />} />
            <Route path="/login" element={<Navigate to="/portal/login" replace />} />
            <Route path="/portal/login" element={<Login />} />
            <Route path="/portal/forgot-password" element={<ForgotPassword />} />

            {/* Portal (protected) */}
            <Route
              path="/portal"
              element={
                <ProtectedRoute>
                  <PortalLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Overview />} />
              <Route path="tickets" element={<Tickets />} />
              <Route path="service-requests" element={<ServiceRequests />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="account-settings" element={<AccountSettings />} />
              <Route path="admin" element={<AdminContent />} />
              <Route path="billing" element={<Billing />} />
              <Route path=":module" element={<ResourceModule />} />
            </Route>

            {/* Hidden staff console */}
            <Route
              path="/cbconsole"
              element={
                <ProtectedRoute>
                  <CbConsole />
                </ProtectedRoute>
              }
            />
          </Routes>
          )}
        </Suspense>
      </BrowserRouter>
      </ContentProvider>
    </AuthProvider>
  );
}

export default App;
